import { Box, Typography, useTheme } from '@mui/material'
import omit from 'lodash/omit'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Slider from 'react-slick'
import 'slick-carousel/slick/slick-theme.css'
import 'slick-carousel/slick/slick.css'

import ENAMEL_STUDIO from '@/assets/json/enamel_studio.json'
import DraggableContainer from '@/components/DraggableContainer'
import LabelWithTooltip from '@/components/LabelWithTooltip'
import Select from '@/components/Select'

import EnamelStudioImageSlider from './EnamelStudioImageSlider'

const CONTAINER_BG =
  'transparent linear-gradient(180deg,#000000d9,#545454f2) 0% 0% no-repeat padding-box'
const PANEL_HEIGHT = 125
export const IMAGE_SIZE = 85

interface EnamelStudioProps {
  dimensions: { width: number; height: number }
  images: any
  setImages: any
  availableHeight: number
}

const settings = {
  variableWidth: true,
  slidesToScroll: 3,
  arrows: true,
  speed: 500,
  cssEase: 'linear',
  draggable: true,
  infinite: false,
}

const EnamelStudio: React.FC<EnamelStudioProps> = ({
  dimensions,
  images,
  setImages,
  availableHeight,
}) => {
  const theme = useTheme()
  const { t } = useTranslation()

  const [brand, setBrand] = useState('')

  const options = useMemo(
    () =>
      Object.keys(ENAMEL_STUDIO)?.map((key) => ({
        text: key,
        value: key,
      })),
    [ENAMEL_STUDIO],
  )
  const onDeleteImage = (name: string) => {
    setImages((prev) => omit(prev, name))
  }

  return (
    <>
      <DraggableContainer
        axis="y"
        bounds="parent"
        defaultPosition={{
          x: 0.1,
          y: availableHeight ? availableHeight - PANEL_HEIGHT : 0,
        }}
        styleContainer={{ width: '100%' }}
      >
        <Box
          flexGrow={1}
          display="flex"
          alignItems="center"
          style={{
            background: CONTAINER_BG,
            height: `${PANEL_HEIGHT}px`,
          }}
        >
          <Box
            width="216px"
            bgcolor={theme.palette.common.black}
            height={`${PANEL_HEIGHT}px`}
            display="flex"
            p={2}
            flexDirection="column"
            gap={1}
          >
            <LabelWithTooltip
              label={t('digitalTryIn.ceramicBrand')}
              tooltipText={t('digitalTryIn.ceramicBrandTooltip')}
              titleSx={{ fontSize: '1rem' }}
            />
            <Select
              options={options}
              name="ceramic-brand"
              onFormValueChange={(_, v) => {
                setBrand(v)
              }}
              placeholder={t('digitalTryIn.brandName')}
              menuStyle={{
                maxHeight: '400px',
              }}
            />
          </Box>

          {!!brand && (
            <Box mx="auto" width="calc(100% - 280px)">
              <Slider {...settings}>
                {ENAMEL_STUDIO[brand as keyof typeof ENAMEL_STUDIO]?.map(
                  (b, i) => (
                    <Box key={i} display="flex" px={4} mt={0.5}>
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        flexDirection="column"
                        onClick={() => {
                          setImages((prev) => ({
                            ...prev,
                            [b.name]: {
                              src: b.src,
                              id: `${b.name}-${Date.now()}`,
                            },
                          }))
                        }}
                      >
                        <img
                          src={b.src}
                          alt={b.name}
                          height={`${IMAGE_SIZE}px`}
                          width={`${IMAGE_SIZE}px`}
                          style={{ cursor: 'pointer' }}
                        />
                        <Typography
                          variant="subtitle2"
                          mt={0.5}
                          textAlign="center"
                        >
                          {b.name}
                        </Typography>
                      </Box>
                    </Box>
                  ),
                )}
              </Slider>
            </Box>
          )}
        </Box>
      </DraggableContainer>

      {Object.entries(images || {})?.map(([key, value]) => (
        <EnamelStudioImageSlider
          key={value.id}
          dimensions={dimensions}
          name={key}
          src={value.src}
          onDeleteImage={onDeleteImage}
        />
      ))}
    </>
  )
}

export default EnamelStudio
